import clsx from 'clsx';
import { memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import z from 'zod';

import { Button } from '@doger/ui/components/ui/button';
import { useJWT } from '@doger/core/modules/auth';
import { Logo } from '@doger/core/shared/ui/logo';

import { useAuth } from '../../auth';
import { ReactComponent as CheckCircleFillIcon } from './icons/check_circle_fill.svg';
import { ReactComponent as CloseCircleFillIcon } from './icons/close_circle_fill.svg';
import { ReactComponent as CompanyCheckIcon } from './icons/company-check.svg';
import { ReactComponent as CostAnalysisIcon } from './icons/cost-analysis.svg';
import { ReactComponent as GeminiIcon } from './icons/gemini.svg';
import { ReactComponent as GrokIcon } from './icons/grok.svg';
import { ReactComponent as OpenAIIcon } from './icons/openai.svg';
import { ReactComponent as ReviewIcon } from './icons/review.svg';
import { ReactComponent as SearchIcon } from './icons/search.svg';
import styles from './empty-chat.module.scss';

type EmptyChatProps = {
  className?: string;
  onSubmit?: (text: string) => void;
  isLoading?: boolean;
};

const examplesSchema = z.array(z.string()).default([]).catch([]);

export const EmptyChat = memo(
  ({ onSubmit, isLoading = false, className }: EmptyChatProps) => {
    const { t } = useTranslation();
    const jwt = useJWT();

    return (
      <div className={clsx(styles.emptyChat, className)}>
        <div
          className={clsx(
            styles.emptyChat__content,
            styles.emptyChat__contentTransitioning
          )}
        >
          <div className={styles.emptyChat__hero}>
            <h2 className={styles.emptyChat__logo}>
              <Logo
                width={160}
                height={160}
                style={{ marginBottom: -16 }}
                name
              />
            </h2>
            <p className={styles.emptyChat__subtitle}>
              <Trans
                i18nKey='onboarding.welcome.subtitle'
                components={{
                  b: <b style={{ fontWeight: 'bold' }} />,
                }}
              />
            </p>
            <div className={styles.emptyChat__closedBeta}>
              <p>{t('onboarding.closedBeta')}</p>
            </div>
          </div>

          <Features />

          <Models />

          {jwt && (
            <>
              <div className={styles.emptyChat__tips}>
                <h3>{t('onboarding.usecase.tips.title')}</h3>
                <div className={styles.emptyChat__tipContent}>
                  <div className={styles.emptyChat__tipContentItem}>
                    <strong>
                      {t('onboarding.usecase.tips.specificity.title')}
                    </strong>
                    <ul>
                      <li>
                        <span>
                          <CheckCircleFillIcon />
                        </span>

                        <span>
                          {t('onboarding.usecase.tips.specificity.good')}
                        </span>
                      </li>

                      <li>
                        <span>
                          <CloseCircleFillIcon />
                        </span>

                        <span>
                          {t('onboarding.usecase.tips.specificity.bad')}
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className={styles.emptyChat__tipContentItem}>
                    <strong>{t('onboarding.usecase.tips.edrpou.title')}</strong>{' '}
                    <ul>
                      <li>
                        <span>
                          <CheckCircleFillIcon />
                        </span>

                        <span>
                          {t('onboarding.usecase.tips.edrpou.description1')}
                        </span>
                      </li>

                      <li>
                        <span>
                          <CheckCircleFillIcon />
                        </span>

                        <span>
                          {t('onboarding.usecase.tips.edrpou.description2')}
                        </span>
                      </li>

                      <li>
                        <span>
                          <CheckCircleFillIcon />
                        </span>

                        <span>
                          {t('onboarding.usecase.tips.edrpou.description3')}
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className={styles.emptyChat__tipContentItem}>
                    <strong>{t('onboarding.usecase.tips.dates.title')}</strong>{' '}
                    <ul>
                      <li>
                        <span>
                          <CheckCircleFillIcon />
                        </span>

                        <span>{t('onboarding.usecase.tips.dates.date')}</span>
                      </li>

                      <li>
                        <span>
                          <CheckCircleFillIcon />
                        </span>

                        <span>{t('onboarding.usecase.tips.dates.range')}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className={styles.emptyChat__suggestions}>
                <h3>{t('suggestions.title')}</h3>

                <ul className={styles.emptyChat__suggestionList}>
                  <li>
                    <CheckCircleFillIcon />
                    <span>{t('suggestions.startWithBuyer')}</span>
                  </li>
                  <li>
                    <CheckCircleFillIcon />
                    <span>{t('suggestions.combineData')}</span>
                  </li>
                  <li>
                    <CheckCircleFillIcon />
                    <span>{t('suggestions.watchRedFlags')}</span>
                  </li>
                  <li>
                    <CheckCircleFillIcon />
                    <span>{t('suggestions.createNewChat')}</span>
                  </li>
                </ul>
              </div>

              <Examples onSubmit={onSubmit} isLoading={isLoading} />
            </>
          )}
        </div>
      </div>
    );
  }
);

const Features = memo(() => {
  const { t } = useTranslation();

  return (
    <div className={styles.emptyChat__capabilities}>
      <h3>{t('onboarding.capabilities.title')}</h3>
      <div className={styles.emptyChat__capabilityGrid}>
        <div className={styles.emptyChat__capability}>
          <div className={styles.emptyChat__capabilityIcon} aria-hidden='true'>
            <SearchIcon />
          </div>
          <div className={styles.emptyChat__capabilityContent}>
            <h4>{t('onboarding.capabilities.searchAnalysis.title')}</h4>
            <p>{t('onboarding.capabilities.searchAnalysis.description')}</p>
          </div>
        </div>

        <div className={styles.emptyChat__capability}>
          <div className={styles.emptyChat__capabilityIcon} aria-hidden='true'>
            <CostAnalysisIcon />
          </div>
          <div className={styles.emptyChat__capabilityContent}>
            <h4>{t('onboarding.capabilities.costAnalysis.title')}</h4>
            <p>{t('onboarding.capabilities.costAnalysis.description')}</p>
          </div>
        </div>

        <div className={styles.emptyChat__capability}>
          <div className={styles.emptyChat__capabilityIcon} aria-hidden='true'>
            <ReviewIcon />
          </div>
          <div className={styles.emptyChat__capabilityContent}>
            <h4>{t('onboarding.capabilities.tenderReview.title')}</h4>
            <p>{t('onboarding.capabilities.tenderReview.description')}</p>
          </div>
        </div>

        <div className={styles.emptyChat__capability}>
          <div className={styles.emptyChat__capabilityIcon} aria-hidden='true'>
            <CompanyCheckIcon />
          </div>
          <div className={styles.emptyChat__capabilityContent}>
            <h4>{t('onboarding.capabilities.companyCheck.title')}</h4>
            <p>{t('onboarding.capabilities.companyCheck.description')}</p>
          </div>
        </div>
      </div>
    </div>
  );
});

const Models = memo(() => {
  const { t } = useTranslation();

  return (
    <div className={styles.emptyChat__models}>
      <h3>{t('onboarding.models.title')}</h3>
      <div className={styles.emptyChat__modelsList}>
        <div className={styles.emptyChat__model}>
          <GrokIcon />
        </div>
        <div className={styles.emptyChat__model}>
          <GeminiIcon />
        </div>
        <div className={styles.emptyChat__model}>
          <OpenAIIcon />
        </div>
      </div>
    </div>
  );
});

type ExamplesProps = {
  onSubmit?: (text: string) => void;
  isLoading?: boolean;
};

const Examples = memo(({ onSubmit, isLoading }: ExamplesProps) => {
  const { setIsAuthModalOpen } = useAuth();
  const jwt = useJWT();
  const { t } = useTranslation();

  const handleExampleClick = (example: string) => {
    if (!jwt) {
      setIsAuthModalOpen(true);

      return;
    }

    if (!isLoading) {
      onSubmit?.(example);
    }
  };

  return (
    <div className={styles.emptyChat__examples}>
      <h3>{t('onboarding.examples.title')}</h3>
      <div className={styles.emptyChat__buttons}>
        {examplesSchema
          .parse(t('onboarding.examples.examples', { returnObjects: true }))
          .map((example) => (
            <Button
              className={styles.emptyChat__button}
              key={example}
              size='lg'
              variant='outlined'
              palette='ghost'
              onClick={() => handleExampleClick(example)}
              disabled={isLoading}
            >
              <span>{example}</span>

              <svg
                width='25'
                height='25'
                viewBox='0 0 25 25'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M6.52227 17.7112C6.21749 17.3686 6.24819 16.8437 6.59085 16.5389L18.1724 6.23756C18.5151 5.93278 19.04 5.96348 19.3447 6.30614C19.6495 6.64881 19.6188 7.17366 19.2762 7.47844L7.69457 17.7798C7.35191 18.0846 6.82706 18.0539 6.52227 17.7112Z'
                  fill='currentColor'
                />
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M18.2192 15.5031C17.7614 15.4763 17.412 15.0834 17.4389 14.6256L17.8483 7.63851L10.8604 7.2297C10.4026 7.20292 10.0532 6.81007 10.08 6.35226C10.1068 5.89445 10.4996 5.54503 10.9574 5.57182L18.7743 6.02913C18.9942 6.04199 19.1984 6.14157 19.3447 6.30614C19.4911 6.47071 19.5677 6.68679 19.5548 6.90665L19.0967 14.7227C19.0699 15.1805 18.677 15.5299 18.2192 15.5031Z'
                  fill='currentColor'
                />
              </svg>
            </Button>
          ))}
      </div>
    </div>
  );
});
